/*
  TAFKA Custom Asset: Shipping Progress Bar
  Extracted from snippets/shipping-progress-bar-portable.liquid so the script
  is loaded globally via layout/theme.liquid (defer) and runs on every page.

  Why global: the Eurus cart drawer is (re-)rendered via the Section Rendering API
  and injected with innerHTML - inline <script> tags inside that HTML never execute.
  The bar instance in the drawer therefore can only be initialised by a script
  that is already running on the page.

  Cart sync: Eurus core (assets/theme.js) and every cart add-on dispatch the
  document event "eurus:cart:items-changed" on every cart mutation (add / quantity
  change / remove), including the drawer +/- buttons. We subscribe to it so that a
  bar rendered OUTSIDE the drawer (e.g. on the product page) recomputes when the
  cart changes from anywhere - not just on page reload. Generic events are kept as
  fallbacks so the component stays portable to other themes.

  Author: Lukas Follert
  Created: 2026-06-05
  Updated: 2026-07-01 - subscribe to eurus:cart:items-changed; refresh all bars
                        from one /cart.js fetch; register triggers once (no leak).
*/
(function () {
  function readConfig(container) {
    return {
      target: parseFloat(container.dataset.spbTarget) || 6,
      trackBy: container.dataset.spbTrackBy || 'weight',
      unit: container.dataset.spbUnit || '',
      msgProgress: container.dataset.spbMsgProgress || 'Add [remaining] more for free shipping!',
      msgComplete: container.dataset.spbMsgComplete || 'Free shipping unlocked!'
    };
  }

  // Paint a single bar from an already-fetched cart object.
  function renderBar(container, cart) {
    var config = readConfig(container);
    var messageEl = container.querySelector('[data-spb-message]');
    var fillEl = container.querySelector('[data-spb-fill]');
    var indicatorEl = container.querySelector('[data-spb-indicator]');
    if (!fillEl || !messageEl) return;

    var current = 0;
    if (config.trackBy === 'weight') {
      current = cart.total_weight / 1000; // grams to kg
    } else if (config.trackBy === 'price') {
      current = cart.total_price / 100; // cents to currency
    } else if (config.trackBy === 'quantity') {
      current = cart.item_count;
    }

    var progress = Math.min((current / config.target) * 100, 100);
    var remaining = Math.max(config.target - current, 0);
    var remainingStr = remaining % 1 === 0 ? remaining.toString() : remaining.toFixed(1);
    var isComplete = current >= config.target;
    var hasItems = current > 0;

    // Fill bar
    fillEl.style.width = progress + '%';
    fillEl.classList.toggle('spb-complete', isComplete);

    // Indicator (optional)
    if (indicatorEl) {
      indicatorEl.style.left = progress + '%';
      indicatorEl.classList.remove('spb-active', 'spb-complete');
      if (isComplete) {
        indicatorEl.classList.add('spb-complete');
      } else if (hasItems) {
        indicatorEl.classList.add('spb-active');
      }
    }

    // Message
    if (isComplete) {
      messageEl.textContent = config.msgComplete;
    } else {
      var remainingWithUnit = config.unit ? remainingStr + ' ' + config.unit : remainingStr;
      messageEl.textContent = config.msgProgress.replace('[remaining]', remainingWithUnit);
    }
  }

  // Update every bar currently in the DOM from ONE cart fetch. Debounced so a
  // burst of cart events collapses into a single request.
  var refreshScheduled = false;
  function refreshAllBars() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    setTimeout(function () {
      refreshScheduled = false;
      var bars = document.querySelectorAll('[data-spb-container]');
      if (!bars.length) return;
      fetch('/cart.js')
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          bars.forEach(function (bar) { renderBar(bar, cart); });
        })
        .catch(function (error) {
          console.error('Shipping progress bar: Failed to fetch cart', error);
        });
    }, 50);
  }

  // Cart-change triggers - registered ONCE per page (not per bar, so re-rendered
  // drawer bars don't pile up duplicate listeners).
  function bindGlobalTriggers() {
    if (window.__spbTriggersBound) return;
    window.__spbTriggersBound = true;

    // Eurus core + add-ons dispatch this on every cart mutation. This is the fix.
    document.addEventListener('eurus:cart:items-changed', refreshAllBars);

    // Generic fallbacks for portability to non-Eurus themes.
    ['cart:updated', 'cart:change', 'ajaxCart:updated', 'cart:item-added', 'theme:cart:change']
      .forEach(function (evt) { document.addEventListener(evt, refreshAllBars); });

    // Last-resort fallback: any add-to-cart click (themes with no cart event).
    document.body.addEventListener('click', function (e) {
      var addToCartSelectors = [
        '[data-add-to-cart]',
        'form[action*="/cart/add"]',
        '.product-form__submit',
        '.add-to-cart',
        '[name="add"]',
        '.btn-add-to-cart'
      ];
      for (var i = 0; i < addToCartSelectors.length; i++) {
        if (e.target.closest(addToCartSelectors[i])) {
          setTimeout(refreshAllBars, 500);
          setTimeout(refreshAllBars, 1000);
          break;
        }
      }
    });
  }

  // First paint for a bar (covers freshly AJAX-injected drawer bars).
  function initShippingBar(container) {
    if (container.__spbInited) return;
    container.__spbInited = true;
    fetch('/cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) { renderBar(container, cart); })
      .catch(function (error) {
        console.error('Shipping progress bar: Failed to fetch cart', error);
      });
  }

  function init() {
    bindGlobalTriggers();
    document.querySelectorAll('[data-spb-container]').forEach(initShippingBar);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan whenever new containers appear (e.g. cart drawer injected via AJAX).
  // Guard so the observer is only set up once per page even if the script loads twice.
  if (!window.__spbObserverActive) {
    window.__spbObserverActive = true;
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) { init(); break; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
